/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import {
  HomefieldIconsLibraryComponent,
  HomefieldIconsLibraryService,
  homefieldIconChevronDownOutline,
} from '@dsg-tech/homefield-angular-icons';
import {
  analyticsSortMethodValues,
  familyPageDropdownValues,
  searchDropdownValues,
  topRatedSort,
} from '@plp/data-access/constants';
import {
  selectProductListSEO,
  selectSelectedSort,
  sortSelectionChanged,
} from '@plp/data-access/state';

import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ProductListLayoutService } from '@plp/data-access/services';
import { Store } from '@ngrx/store';

@Component({
  selector: 'plp-components-sort',
  imports: [HomefieldIconsLibraryComponent],
  templateUrl: './sort.component.html',
  styleUrl: './sort.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SortComponent {
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly store = inject(Store);
  private readonly productListLayoutService = inject(ProductListLayoutService);
  private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
  readonly isDisabled = input(false);
  readonly sortByCustomerRatingEnabled = input(false);

  readonly tabPressed = output<void>();

  readonly seoData = this.store.selectSignal(selectProductListSEO);
  readonly dropdownValues: Signal<{ label: string; value: number }[]> = computed(() => {
    if (this.seoData()?.catgroupId) {
      return this.sortByCustomerRatingEnabled()
        ? [...familyPageDropdownValues, topRatedSort]
        : familyPageDropdownValues;
    } else {
      return searchDropdownValues;
    }
  });

  readonly defaultValue: Signal<{ label: string; value: number }> =
    this.store.selectSignal(selectSelectedSort);

  readonly selectedValue = computed(() => {
    const currentValue = this.defaultValue();
    const valueExistsInDropdown = this.dropdownValues().some(
      (option) => option.value === currentValue?.value,
    );
    if (!currentValue || !valueExistsInDropdown) {
      return this.dropdownValues()[0];
    }
    return currentValue;
  });

  constructor() {
    this.homefieldIconsLibraryService.registerIcons([homefieldIconChevronDownOutline]);
  }

  selectValue(event: Event): void {
    // Since a <select> element can have multiple selections if it's configured
    // for multiple selection (<select multiple>),
    // we use [0] to target the first (and only) selected option.
    const selector = event.target as HTMLSelectElement;
    const selectedOption = selector?.selectedOptions[0];

    const selectedLabel = selectedOption?.label;
    const selectedValue = selectedOption?.value;

    this.store.dispatch(
      sortSelectionChanged({
        selectedSort: { label: selectedLabel, value: Number.parseInt(selectedValue) },
      }),
    );
    const analyticsValue =
      analyticsSortMethodValues.find(
        (dropdownOption) => Number.parseInt(selectedValue) === dropdownOption.value,
      )?.text ?? '';
    if (analyticsValue) {
      this.setAnalyticsEvent(analyticsValue);
    }
    selector?.blur();
    this.productListLayoutService.scrollToTop();
  }

  onTabKeyDown(): void {
    this.tabPressed.emit();
  }

  private setAnalyticsEvent(sortValue: string): void {
    const refinementUpdateObj = { UpdateType: 'SortMethod', UpdateValue: sortValue };
    this.analyticsService.triggerAnalyticsEvent('ResultsViewUpdate', refinementUpdateObj);
  }
}
